import _, {chain, find, isUndefined} from 'lodash';

import {ErrorWithData, UnreachableError} from '../../../../../libs/shared/helpers/src/types/customErrors';
import {reportToBugsnag} from '../../../../../srcCommon/services/bugsnag/bugsnag';
import {CellsConversationsBoundary, CellsMessagesBoundary} from '../../../../helpers/cellsHelpers';
import {teammateCanAccessInbox} from '../../../../helpers/modelHelpers';
import {
  ConversationStatusesEnum,
  findAliveConversationTracker,
  findConversationsReminder,
  findConversationStatus,
  isConversationAssigned,
  isInternalConversation,
} from '../../../../helpers/models/conversationHelpers';
import {isDirtyDraftMessage, isUntouchedDraftMessage} from '../../../../helpers/models/messageHelpers';
import {DraftCommentModel} from '../../../../models/api/commentModel';
import {ConversationTeamStatusesEnum} from '../../../../models/api/conversationCommonTypes';
import {ConversationBaseModel} from '../../../../models/api/conversationModel';
import {
  ConversationTrackerModel,
  ConversationTrackerStagesEnum,
  ConversationTrackerStatusesEnum,
} from '../../../../models/api/conversationTrackerModel';
import {
  ConversationUpdatedThinEvent,
  isConversationUpdatedThinEventMaybeAssigned,
} from '../../../../models/api/conversationUpdatedThinEventModel';
import {MessageModel, MessageStatusesEnum} from '../../../../models/api/messageModel';
import {TeammateModel} from '../../../../models/api/teammateModel';
import {
  InboxesFiltersEnum,
  TeammatesFoldersEnum,
  TeammatesMessageFoldersEnum,
  TeamsFoldersEnum,
} from '../../../../models/url/inboxesUrlModel';
import {AppState} from '../../../appState';
import {getConversations, getMessages} from '../../../data/dataSelectors';
import {DataState} from '../../../data/dataState';
import {
  InboxesConversationsParameters,
  InboxesGlobalParameters,
  InboxesInboxParameters,
  InboxesParameters,
  InboxesParametersTypesEnum,
  InboxesTeamLensParameters,
  InboxesTeammateInboxParameters,
  InboxesTeammateLensParameters,
  InboxesTeammateMessagesParameters,
  InboxesTeammateParameters,
  InboxesTeammateViewParameters,
  InboxesTeamParameters,
  InboxesUserParameters,
  InboxesViewParameters,
} from '../../inboxes/parameters/inboxesParameters';
import {getCellsPanelContexts} from '../cellsPanelSelectors';
import {CellsPanelPageState, CellsPanelPageTypesEnum} from '../cellsPanelState';
import {CellsContext} from '../context/cellsContext';

/** Check if a conversation matches the specified parameters. */
export function isConversationInPanel(
  dataState: DataState,
  cellsContext: CellsContext,
  parameters: InboxesConversationsParameters,
  conversation: ConversationBaseModel,
): boolean {
  const {teammateId} = cellsContext;
  const isVisible = conversation.teamStatus !== ConversationTeamStatusesEnum.INVISIBLE;

  // Is the conversation even visible?
  if (!isVisible) {
    return false;
  }

  switch (parameters.type) {
    case InboxesParametersTypesEnum.INBOX:
      return isConversationInInbox(cellsContext, parameters, conversation);

    case InboxesParametersTypesEnum.TEAMMATE_INBOX:
      return isConversationInTeammateInbox(cellsContext, dataState, parameters, conversation, teammateId);

    case InboxesParametersTypesEnum.USER:
      return isConversationInUser(dataState, cellsContext, parameters, conversation);

    case InboxesParametersTypesEnum.TEAMMATE:
      return isConversationInTeammate(cellsContext, parameters, conversation);

    case InboxesParametersTypesEnum.TEAM:
      return isConversationInTeam(dataState, cellsContext, parameters, conversation);

    case InboxesParametersTypesEnum.TEAMMATE_LENS:
    case InboxesParametersTypesEnum.TEAM_LENS:
      return isConversationInLens(dataState, cellsContext, parameters, conversation);

    case InboxesParametersTypesEnum.GLOBAL:
      return isConversationInAssigned(dataState, cellsContext, parameters, conversation);

    case InboxesParametersTypesEnum.VIEW:
    case InboxesParametersTypesEnum.TEAMMATE_VIEW:
      return isConversationInView(cellsContext, parameters, conversation);

    default:
      throw new UnreachableError(parameters);
  }
}

/** Check if a conversation belongs in the specified inbox. */
function isConversationInInbox(
  cellsContext: CellsContext,
  parameters: InboxesInboxParameters,
  conversation: ConversationBaseModel,
): boolean {
  return (
    conversationMatchesFilters(cellsContext, parameters.filters, conversation) &&
    conversation.inboxIds.includes(parameters.inboxId)
  );
}

function isConversationInTeammateInbox(
  cellsContext: CellsContext,
  dataState: DataState,
  parameters: InboxesTeammateInboxParameters,
  conversation: ConversationBaseModel,
  delegatedTeammateId: number,
): boolean {
  const {teammates, delegations} = dataState;
  const teammate = teammates[delegatedTeammateId];
  if (!teammate) {
    return false;
  }

  const isAssignee = conversation.assigneeId === delegatedTeammateId;
  const hasInboxTag = conversation.tagIds.includes(teammate.inboxTagId);

  const delegation = find(delegations, (d) => Boolean(d && d.ownerId === teammate.id));
  const isPartialDelegation = delegation ? !delegation.isFull : false;

  // TEMPORARY: Until the Inbox tag is returned on light delegations.
  const hasValidTags = hasInboxTag || isPartialDelegation;

  return (
    (!isAssignee || isPartialDelegation) &&
    conversationMatchesTrackerFilters(
      cellsContext,
      parameters.filters || [InboxesFiltersEnum.OPEN],
      conversation,
    ) &&
    conversation.inboxIds.includes(parameters.inboxId) &&
    hasValidTags
  );
}

/** Check if a conversation belongs in the user view. */
function isConversationInUser(
  dataState: DataState,
  cellsContext: CellsContext,
  parameters: InboxesUserParameters,
  conversation: ConversationBaseModel,
): boolean {
  // Check if this teammate is a tracker.
  const {teammateId, folder} = parameters;

  // Never match destroyed conversations.
  if (conversation.teamStatus === ConversationTeamStatusesEnum.DESTROYED) {
    return false;
  }

  const {teammates} = dataState;
  const teammate = teammates[teammateId];
  if (!teammate) {
    return false;
  }

  const tracker = findAliveConversationTracker(conversation, teammateId);

  if (!tracker) {
    return false;
  }

  // If the status is invisible, only the "draft" view is a match.
  if (conversation.teamStatus === ConversationTeamStatusesEnum.INVISIBLE) {
    return false;
  }

  const hasInboxTag = conversation.tagIds.includes(teammate.inboxTagId);
  const matchesFilters = conversationMatchesTrackerFilters(cellsContext, parameters.filters, conversation);

  // If not, check if it should be displayed in this view.
  switch (folder) {
    case TeammatesFoldersEnum.INBOX:
      return hasInboxTag && matchesFilters;

    case TeammatesFoldersEnum.ASSIGNED:
    case TeammatesFoldersEnum.ASSIGNED_SHARED:
      return hasInboxTag && tracker.stage === ConversationTrackerStagesEnum.ASSIGNEE && matchesFilters;

    case TeammatesFoldersEnum.SHARED_WITH_ME:
      return (
        hasInboxTag &&
        tracker.stage !== ConversationTrackerStagesEnum.ASSIGNEE &&
        !isInternalConversation(conversation) &&
        !isConversationInOwnedInbox(dataState, conversation, teammate) &&
        matchesFilters
      );

    case TeammatesFoldersEnum.DISCUSSIONS:
      return (
        hasInboxTag &&
        tracker.stage !== ConversationTrackerStagesEnum.ASSIGNEE &&
        isInternalConversation(conversation) &&
        matchesFilters
      );

    case TeammatesFoldersEnum.ALL:
      return true;

    default:
      return false;
  }
}

/** Check if a conversation belongs in teammate's view. */
function isConversationInTeammate(
  cellsContext: CellsContext,
  parameters: InboxesTeammateParameters,
  conversation: ConversationBaseModel,
): boolean {
  const {teammateId, filters} = parameters;

  // The teammate should be the assignee.
  if (teammateId !== conversation.assigneeId) {
    return false;
  }

  // The filter should match.
  return conversationMatchesFilters(cellsContext, filters, conversation);
}

/** Check if a conversation is in any of the teammate's inboxes. */
function isConversationInOwnedInbox(
  dataState: DataState,
  conversation: ConversationBaseModel,
  teammate: TeammateModel,
): boolean {
  return chain(conversation.inboxIds)
    .map((id) => dataState.inboxes.byId[id])
    .compact()
    .some((i) => i.namespace === teammate.namespace)
    .value();
}

/** Check if a conversation belongs in the specified team. */
function isConversationInTeam(
  dataState: DataState,
  cellsContext: CellsContext,
  parameters: InboxesTeamParameters,
  conversation: ConversationBaseModel,
): boolean {
  // Find the namespace of the specified team.
  const {userId, teammates, preferences, teams} = dataState;
  const user = teammates[userId];
  if (!user) {
    return false;
  }

  const team = teams[parameters.teamId];
  if (!team) {
    return false;
  }

  const {visibleInboxIds} = preferences;
  const isInTeam = conversation.inboxIds.some((i) => {
    const inbox = dataState.inboxes.byId[i];
    if (!inbox) {
      return false;
    }

    return (
      inbox.namespace === team.namespace &&
      teammateCanAccessInbox(user, inbox) &&
      visibleInboxIds.includes(inbox.id)
    );
  });

  if (!isInTeam) {
    return false;
  }

  switch (parameters.folder) {
    // TODO: Remove when the server is updated to only return trashed items in "trash".
    case TeamsFoldersEnum.TRASHED:
      return [ConversationTeamStatusesEnum.TRASHED, ConversationTeamStatusesEnum.SPAM].includes(
        conversation.teamStatus,
      );

    case TeamsFoldersEnum.SPAM:
      return conversation.teamStatus === ConversationTeamStatusesEnum.SPAM;

    case TeamsFoldersEnum.INBOX:
    default:
      return conversationMatchesFilters(cellsContext, parameters.filters, conversation);
  }
}

function isConversationInView(
  cellsContext: CellsContext,
  parameters: InboxesViewParameters | InboxesTeammateViewParameters,
  conversation: ConversationBaseModel,
): boolean {
  const isInView = conversation.tagIds.some((id) => parameters.viewId === id);
  if (!isInView) {
    return false;
  }

  return conversationMatchesFilters(cellsContext, parameters.filters, conversation);
}

function isConversationInLens(
  dataState: DataState,
  cellsContext: CellsContext,
  parameters: InboxesTeammateLensParameters | InboxesTeamLensParameters,
  conversation: ConversationBaseModel,
): boolean {
  const lens = dataState.lenses[parameters.lensId];
  const hasMatchingInbox = lens
    ? conversation.inboxIds.some((inboxId) => lens.inboxIds.includes(inboxId))
    : false;
  if (!hasMatchingInbox) {
    return false;
  }

  return conversationMatchesFilters(cellsContext, parameters.filters, conversation);
}

function isConversationInAssigned(
  dataState: DataState,
  cellsContext: CellsContext,
  parameters: InboxesGlobalParameters,
  conversation: ConversationBaseModel,
): boolean {
  const {userId, preferences} = dataState;
  const {displayedTeammateIds} = preferences;

  // The conversation should be assigned.
  if (!isConversationAssigned(conversation)) {
    return false;
  }

  const isAssigneeUser = userId === conversation.assigneeId;
  const isAssigneeHidden = displayedTeammateIds && !displayedTeammateIds.includes(conversation.assigneeId);

  // We only want visible teammates.
  if (isAssigneeUser || isAssigneeHidden) {
    return false;
  }

  // It should match the filters.
  return conversationMatchesFilters(cellsContext, parameters.filters, conversation);
}

/** Check if a conversation matches one of the specified filters. */
function conversationMatchesFilters(
  cellsContext: CellsContext,
  filters: ReadonlyArray<InboxesFiltersEnum>,
  conversation: ConversationBaseModel,
): boolean {
  return filters.some((f) => conversationMatchesFilter(cellsContext, f, conversation));
}

/** Check if a conversation matches the specified filter. */
function conversationMatchesFilter(
  cellsContext: CellsContext,
  filter: InboxesFiltersEnum,
  conversation: ConversationBaseModel,
): boolean {
  const status = findConversationStatus(cellsContext, conversation);
  const isOpen = status === ConversationStatusesEnum.OPEN;
  const isArchived = status === ConversationStatusesEnum.ARCHIVED;
  const isAll = isOpen || isArchived;
  const hasReminder = Boolean(findConversationsReminder(cellsContext, [conversation]));

  switch (filter) {
    case InboxesFiltersEnum.UNASSIGNED:
      return isOpen && isUndefined(conversation.assigneeId);

    case InboxesFiltersEnum.ASSIGNED:
      return isOpen && !isUndefined(conversation.assigneeId);

    case InboxesFiltersEnum.OPEN:
      return isOpen;

    case InboxesFiltersEnum.ALL:
      return isAll;

    case InboxesFiltersEnum.SPAM:
      return status === ConversationStatusesEnum.SPAM;

    case InboxesFiltersEnum.TRASHED:
      return status === ConversationStatusesEnum.TRASHED;

    case InboxesFiltersEnum.ARCHIVED:
      return !hasReminder && isArchived;

    case InboxesFiltersEnum.SNOOZED:
      return hasReminder && isArchived;

    default:
      throw new UnreachableError(filter);
  }
}

/** Check if a conversation matches one of the specified tracker filters. */
function conversationMatchesTrackerFilters(
  cellsContext: CellsContext,
  filters: ReadonlyArray<InboxesFiltersEnum>,
  conversation: ConversationBaseModel,
): boolean {
  return filters.some((f) => conversationMatchesTrackerFilter(cellsContext, f, conversation));
}

/** Check if a conversation matches the specified tracker filter. */
function conversationMatchesTrackerFilter(
  cellsContext: CellsContext,
  filter: InboxesFiltersEnum,
  conversation: ConversationBaseModel,
): boolean {
  const {teammateId} = cellsContext;

  // Find the tracker for the specified teammate.
  const tracker = conversation.trackers[teammateId];
  if (!tracker || tracker.stage === ConversationTrackerStagesEnum.NONE) {
    return false;
  }

  // Check if its status matches the specified filter.
  const isOpen = tracker.status === ConversationTrackerStatusesEnum.INBOX;
  const isArchived = tracker.status === ConversationTrackerStatusesEnum.ARCHIVED;
  const isAll = isOpen || isArchived;
  const hasReminder = Boolean(findConversationsReminder(cellsContext, [conversation]));

  switch (filter) {
    case InboxesFiltersEnum.UNASSIGNED:
      return isOpen && isUndefined(conversation.assigneeId);

    case InboxesFiltersEnum.ASSIGNED:
      return isOpen && !isUndefined(conversation.assigneeId);

    case InboxesFiltersEnum.OPEN:
      return isOpen;

    case InboxesFiltersEnum.ALL:
      return isAll;

    case InboxesFiltersEnum.SPAM:
      return tracker.status === ConversationTrackerStatusesEnum.SPAM;

    case InboxesFiltersEnum.TRASHED:
      return tracker.status === ConversationTrackerStatusesEnum.TRASHED;

    case InboxesFiltersEnum.ARCHIVED:
      return isArchived && !hasReminder;

    case InboxesFiltersEnum.SNOOZED:
      return isArchived && hasReminder;

    default:
      throw new UnreachableError(filter);
  }
}

export function shouldConversationBeHiddenFromPanel(
  parameters: InboxesParameters,
  cellsContext: CellsContext,
  conversation: ConversationBaseModel,
  previousConversation: ConversationBaseModel | undefined,
): boolean {
  // If we don't have a previous conversation to compare against, nothing to do.
  if (!previousConversation) {
    return false;
  }

  // If we're not in a conversation-based list, nothing to do.
  if (parameters.type === InboxesParametersTypesEnum.TEAMMATE_MESSAGES) {
    return false;
  }

  const {filters} = parameters;
  const inboxTypes = [InboxesParametersTypesEnum.TEAMMATE_INBOX, InboxesParametersTypesEnum.USER];
  const isOpen = findConversationStatus(cellsContext, conversation) === ConversationStatusesEnum.OPEN;

  if (
    parameters.type === InboxesParametersTypesEnum.VIEW ||
    parameters.type === InboxesParametersTypesEnum.TEAMMATE_VIEW
  ) {
    return (
      previousConversation.tagIds.includes(parameters.viewId) &&
      !conversation.tagIds.includes(parameters.viewId)
    );
  }

  // If we're in the open filter in the user workspace, we have different possibilities.
  if (filters.includes(InboxesFiltersEnum.OPEN) && inboxTypes.includes(parameters.type)) {
    const isAssigningToSelf =
      previousConversation.assigneeId !== conversation.assigneeId &&
      conversation.assigneeId === cellsContext.teammateId;

    // If the user is assigning to themselves in their inbox folder, then it doesn't make sense to hide the conversation.
    if (
      isAssigningToSelf &&
      parameters.type === InboxesParametersTypesEnum.USER &&
      parameters.folder === TeammatesFoldersEnum.INBOX
    ) {
      return false;
    }

    // If the conversation lost its assignee, then make sure we're not in the archived filter.
    if (!!previousConversation.assigneeId && !conversation.assigneeId) {
      return !filters.includes(InboxesFiltersEnum.ARCHIVED);
    }

    // Otherwise, check if the conversation got assigned to a teammate that's not the user.
    return !previousConversation.assigneeId && !!conversation.assigneeId;
  }

  // If we're in a unassigned list, check whether or not the conversation got an assignee.
  if (filters.includes(InboxesFiltersEnum.UNASSIGNED) && !filters.includes(InboxesFiltersEnum.ASSIGNED)) {
    return isOpen && !previousConversation.assigneeId && !!conversation.assigneeId;
  }

  // If we're in the assigned filter, check whether or not the conversation lost its assignee.
  if (filters.includes(InboxesFiltersEnum.ASSIGNED) && !filters.includes(InboxesFiltersEnum.UNASSIGNED)) {
    return isOpen && !!previousConversation.assigneeId && !conversation.assigneeId;
  }

  // If we're in the archived view, check whether or not the assignee changed.
  if (filters.includes(InboxesFiltersEnum.ARCHIVED) && !filters.includes(InboxesFiltersEnum.ASSIGNED)) {
    return previousConversation.assigneeId !== conversation.assigneeId;
  }

  // Otherwise, nothing to do.
  return false;
}

/** Check if a message matches the specified parameters. */
export function isMessageInPanel(
  dataState: DataState,
  parameters: InboxesTeammateMessagesParameters,
  message: MessageModel,
  draftComment?: DraftCommentModel,
): boolean {
  const {userId} = dataState;

  switch (parameters.folder) {
    case TeammatesMessageFoldersEnum.DRAFTS:
      return isMessageInDrafts(userId, message, draftComment);

    case TeammatesMessageFoldersEnum.SENT:
      return isMessageInSent(userId, message, parameters.hasListSendingMessagesInSentFolder);

    case TeammatesMessageFoldersEnum.OUTBOX:
      return isMessageInOutbox(userId, message);

    default:
      throw new UnreachableError(parameters.folder);
  }
}

const draftFolderStatuses = new Set([
  MessageStatusesEnum.DRAFT_COMPOSE,
  MessageStatusesEnum.DRAFT_LATER,
  MessageStatusesEnum.DRAFT_FAILED,
]);

function isMessageInDrafts(userId: number, message: MessageModel, draftComment?: DraftCommentModel): boolean {
  if (isUntouchedDraftMessage(message)) {
    return false;
  }

  const {authorId, authorIds = []} = message;
  const isUserAuthor = authorId === userId || authorIds.includes(userId);
  const messageHasDraftComment = draftComment !== undefined;
  return (isUserAuthor && draftFolderStatuses.has(message.status)) || messageHasDraftComment;
}

function isMessageInSent(
  userId: number,
  message: MessageModel,
  hasListSendingMessagesInSentFolder: boolean,
): boolean {
  const sentStatuses = hasListSendingMessagesInSentFolder
    ? [MessageStatusesEnum.SENT, MessageStatusesEnum.DRAFT_SENDING]
    : [MessageStatusesEnum.SENT];
  return message.authorId === userId && sentStatuses.includes(message.status);
}

const outboxFolderStatuses = new Set([MessageStatusesEnum.DRAFT_PENDING]);

export function isMessageInOutbox(userId: number, message: MessageModel): boolean {
  const {authorId, authorIds = []} = message;
  const isUserAuthor = authorId === userId || authorIds.includes(userId);
  return isUserAuthor && outboxFolderStatuses.has(message.status);
}

const minDiscrepantConversationAge = 3 * 60 * 1000; // ms
const reportedDiscrepantConversationIds = new Set<number>();

export function reportDynamicP2Discrepancies(
  state: AppState,
  delegatedTeammateId: number,
  parameters: InboxesConversationsParameters,
  referenceBoundary: CellsConversationsBoundary | undefined,
  clientConversationIds: ReadonlyArray<number>,
  serverTopBoundary: CellsConversationsBoundary | undefined,
  serverBottomBoundary: CellsConversationsBoundary | undefined,
  serverConversationIds: ReadonlyArray<number>,
  serverTime: number,
): Array<number> {
  const allConversations = getConversations(state);
  const skippedDiscrepantConversationsIds: Array<number> = [];
  const discrepantConversations = _(clientConversationIds)
    .xor(serverConversationIds)
    // The reference boundary might be included because we don't support open intervals + fetch from P2s that are
    // supersets, but that doesn't mean it belongs in this list and so the discrepancy is expected.
    .filter((id) => !referenceBoundary || id !== referenceBoundary.id)
    .map((id) => {
      // Determine the type of discrepancy.
      const type = clientConversationIds.includes(id) ? 'addition' : 'deletion';

      // Find the conversation.
      const conversation = allConversations[id];
      if (!conversation) {
        return {
          type,
          id,
        };
      }

      // Find the tracker.
      const tracker = conversation.trackers[delegatedTeammateId];

      // Don't count conversations that we changed or bumped in the last three minutes as a discrepancy,
      // because we know the server has no read-your-writes consistency.
      // TODO(damien): make sure we have a "remote update" prop on overlays for this use-case
      const age = serverTime - Math.max(conversation.updatedAt, tracker ? tracker.bumpedAt : 0);
      const isDiscrepancy = age >= minDiscrepantConversationAge;
      if (!isDiscrepancy) {
        return undefined;
      }

      // The most common P2 discrepancy is seeing archived/trashed conversations in the user inbox folder, which is
      // always a bad search doc BE issue.
      if (tracker && isBadSearchDocP2Discrepancy(parameters, tracker)) {
        skippedDiscrepantConversationsIds.push(id);
        return undefined;
      }

      return {
        type,
        id,
        reminders: conversation.reminders.filter((reminder) => reminder.teammateId === delegatedTeammateId),
        tracker,
        updatedAt: conversation.updatedAt,
      };
    })
    .compact()
    // Report once per conversation ID per session, to keep reports actionable.
    .filter(({id}) => !reportedDiscrepantConversationIds.has(id))
    .value();

  if (discrepantConversations.length === 0) {
    return skippedDiscrepantConversationsIds;
  }

  reportToBugsnag(
    new ErrorWithData('Detected dynamic P2 discrepancy', {
      parameters,
      boundaries: [serverTopBoundary, serverBottomBoundary],
      conversations: discrepantConversations,
    }),
  );

  discrepantConversations.forEach(({id}) => {
    reportedDiscrepantConversationIds.add(id);
  });

  return [...skippedDiscrepantConversationsIds, ...discrepantConversations.map(({id}) => id)];
}

function isBadSearchDocP2Discrepancy(
  parameters: InboxesConversationsParameters,
  tracker: ConversationTrackerModel,
) {
  const isOpenFilter = parameters.filters.length === 1 && parameters.filters[0] === InboxesFiltersEnum.OPEN;
  const isOpenStatus = tracker.status === ConversationTrackerStatusesEnum.INBOX;

  return isOpenFilter && !isOpenStatus;
}

const minDiscrepantMessageAge = 1 * 60 * 1000; // ms
const reportedDiscrepantMessageUids = new Set<string>();

export function reportDynamicP2MessagesDiscrepancies(
  state: AppState,
  parameters: InboxesTeammateMessagesParameters,
  clientMessageUids: ReadonlyArray<string>,
  serverTopBoundary: CellsMessagesBoundary | undefined,
  serverBottomBoundary: CellsMessagesBoundary | undefined,
  serverMessageUids: ReadonlyArray<string>,
  serverTime: number,
) {
  // This feature flag will always cause discrepancies because we filter out empty comments on the client.
  if (parameters.hasFindMyDrafts2) {
    return;
  }

  const allMessages = getMessages(state);
  const allConversations = getConversations(state);
  const discrepantMessages = _(clientMessageUids)
    .xor(serverMessageUids)
    .map((uid) => {
      // Determine the type of discrepancy.
      const type = clientMessageUids.includes(uid) ? 'addition' : 'deletion';

      // Find the message.
      const message = allMessages[uid];
      if (!message) {
        return {
          type,
          uid,
        };
      }

      // Don't count messages that we changed in the last minute, that are not saved, or that failed to save.
      if (
        (!isUndefined(message.updatedAt) && serverTime - message.updatedAt < minDiscrepantMessageAge) ||
        isUntouchedDraftMessage(message) ||
        isDirtyDraftMessage(message)
      ) {
        return undefined;
      }

      return {
        type,
        uid,
        conversationId: message.conversationId,
        hasConversation: !isUndefined(allConversations[message.conversationId]),
        status: message.status,
        authorId: message.authorId,
        updatedAt: message.updatedAt,
      };
    })
    .compact()
    // Report once per message UID per session, to keep reports actionable.
    .filter(({uid}) => !reportedDiscrepantMessageUids.has(uid))
    .value();
  if (discrepantMessages.length === 0) {
    return;
  }

  reportToBugsnag(
    new ErrorWithData('Detected dynamic P2 messages discrepancy', {
      parameters,
      boundaries: [serverTopBoundary, serverBottomBoundary],
      messages: discrepantMessages,
    }),
  );

  discrepantMessages.forEach(({uid}) => {
    reportedDiscrepantMessageUids.add(uid);
  });
}

// TODO: Refactor to reduce duplication with isConversationInPanel.
export function shouldAcceptConversationUpdatedThinEvent(
  dataState: DataState,
  teammateId: number,
  cellsPage: CellsPanelPageState,
  event: ConversationUpdatedThinEvent,
): boolean {
  // In a message context, drop all updates.
  if (cellsPage.type === CellsPanelPageTypesEnum.MESSAGES) {
    return false;
  }

  const {parameters} = cellsPage;

  // In a search context, accept updates for pre-existing conversations.
  if (parameters.search) {
    return !event.isNewlyVisible && cellsPage.conversationIds.includes(event.conversationId);
  }

  const conversation = dataState.conversations.byId[event.conversationId];

  switch (parameters.type) {
    // Accept updates in the current inbox.
    case InboxesParametersTypesEnum.INBOX:
    case InboxesParametersTypesEnum.TEAMMATE_INBOX:
      return (
        isUndefined(event.inboxIds) ||
        _(event.inboxIds)
          .concat(conversation?.inboxIds ?? [])
          .includes(parameters.inboxId)
      );

    // Accept updates when the user is/was a tracker.
    case InboxesParametersTypesEnum.USER:
      return shouldAcceptConversationUpdatedThinEventForUser(dataState, teammateId, event);

    // Accept updates when the teammate is/was an assignee.
    case InboxesParametersTypesEnum.TEAMMATE:
      return (
        isUndefined(event.assigneeId) ||
        event.assigneeId === parameters.teammateId ||
        conversation?.assigneeId === parameters.teammateId
      );

    // Accept updates in the current tag/view.
    case InboxesParametersTypesEnum.VIEW:
    case InboxesParametersTypesEnum.TEAMMATE_VIEW:
      return (
        isUndefined(event.tagIds) ||
        _(event.tagIds)
          .concat(conversation?.tagIds ?? [])
          .includes(parameters.viewId)
      );

    // Accept updates in accessible inboxes in the current team.
    case InboxesParametersTypesEnum.TEAM: {
      // Accept updates when no inbox metadata is included.
      if (isUndefined(event.inboxIds)) {
        return true;
      }

      const {teammates, teams, inboxes} = dataState;
      const teammate = teammates[teammateId];
      const team = teams[parameters.teamId];
      if (!teammate || !team) {
        return true;
      }

      // TODO: Keep only for pinned inboxes.
      return _(event.inboxIds)
        .concat(conversation?.inboxIds ?? [])
        .some((inboxId) => {
          const inbox = inboxes.byId[inboxId];
          return inbox?.namespace === team.namespace && teammateCanAccessInbox(teammate, inbox);
        });
    }

    // Accept updates for all assigned conversations.
    case InboxesParametersTypesEnum.GLOBAL:
      // TODO: Keep only for pinned teammates.
      return isConversationUpdatedThinEventMaybeAssigned(event) || isConversationAssigned(conversation);

    // In any other context, accept all updates.
    default:
      return true;
  }
}

export function shouldAcceptConversationUpdatedThinEventForUser(
  dataState: DataState,
  teammateId: number,
  event: ConversationUpdatedThinEvent,
): boolean {
  return (
    isUndefined(event.trackerTeammateIds) ||
    event.trackerTeammateIds.includes(teammateId) ||
    Boolean(findAliveConversationTracker(dataState.conversations.byId[event.conversationId], teammateId))
  );
}

export function isConversationInCells(state: AppState, conversationId: number): boolean {
  return getCellsPanelContexts(state).some(
    (context) =>
      context.page.type === CellsPanelPageTypesEnum.CONVERSATIONS &&
      context.page.conversationIds.includes(conversationId),
  );
}
